/**
 * Admin Restaurant Service
 * Handles fetching restaurant operational data for admin dashboard
 * Provides cross-tenant access to orders, menu, tables, staff, and analytics
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db } from 'foodhub-shared/firebase';

// Collection names
const ORDERS_COLLECTION = 'orders';
const MENU_ITEMS_COLLECTION = 'menuItems';
const MENU_CATEGORIES_COLLECTION = 'menuCategories';
const TABLES_COLLECTION = 'tables';
const STAFF_COLLECTION = 'staff';
const DAILY_SALES_COLLECTION = 'dailySales';

/**
 * Get all orders for a specific tenant
 */
export const getTenantOrders = async (tenantId: string) => {
  try {
    const q = query(
      collection(db, ORDERS_COLLECTION),
      where('tenantId', '==', tenantId)
    );
    const querySnapshot = await getDocs(q);
    
    const orders: any[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      orders.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      });
    });

    return orders;
  } catch (error: any) {
    console.error('Get tenant orders error:', error);
    return [];
  }
};

/**
 * Get menu items for a specific tenant
 */
export const getTenantMenuItems = async (tenantId: string) => {
  try {
    const q = query(
      collection(db, MENU_ITEMS_COLLECTION),
      where('tenantId', '==', tenantId)
    );
    const querySnapshot = await getDocs(q);
    
    const items: any[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      items.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
      });
    });

    return items;
  } catch (error: any) {
    console.error('Get tenant menu items error:', error);
    return [];
  }
};

/**
 * Get menu categories for a specific tenant
 */
export const getTenantMenuCategories = async (tenantId: string) => {
  try {
    const q = query(
      collection(db, MENU_CATEGORIES_COLLECTION),
      where('tenantId', '==', tenantId)
    );
    const querySnapshot = await getDocs(q);
    
    const categories: any[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      categories.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
      });
    });

    return categories;
  } catch (error: any) {
    console.error('Get tenant menu categories error:', error);
    return [];
  }
};

/**
 * Get tables for a specific tenant
 */
export const getTenantTables = async (tenantId: string) => {
  try {
    const q = query(
      collection(db, TABLES_COLLECTION),
      where('tenantId', '==', tenantId)
    );
    const querySnapshot = await getDocs(q);
    
    const tables: any[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      tables.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
      });
    });

    return tables;
  } catch (error: any) {
    console.error('Get tenant tables error:', error);
    return [];
  }
};

/**
 * Get staff for a specific tenant
 */
export const getTenantStaff = async (tenantId: string) => {
  try {
    const q = query(
      collection(db, STAFF_COLLECTION),
      where('tenantId', '==', tenantId)
    );
    const querySnapshot = await getDocs(q);
    
    const staff: any[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      staff.push({
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        lastLogin: data.lastLogin?.toDate?.(),
      });
    });

    return staff;
  } catch (error: any) {
    console.error('Get tenant staff error:', error);
    return [];
  }
};

/**
 * Get daily sales for a specific tenant
 */
export const getTenantDailySales = async (tenantId: string, date: string) => {
  try {
    const docRef = doc(db, DAILY_SALES_COLLECTION, `${tenantId}_${date}`);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      ...docSnap.data(),
      id: docSnap.id,
    };
  } catch (error: any) {
    console.error('Get tenant daily sales error:', error);
    return null;
  }
};

/**
 * Get restaurant statistics for a specific tenant
 */
export const getTenantStats = async (tenantId: string) => {
  try {
    const [orders, tables, staff, menuItems] = await Promise.all([
      getTenantOrders(tenantId),
      getTenantTables(tenantId),
      getTenantStaff(tenantId),
      getTenantMenuItems(tenantId),
    ]);

    // Calculate stats
    const totalOrders = orders.length;
    const completedOrders = orders.filter(o => o.status === 'completed' || o.status === 'paid').length;
    const totalRevenue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
    const occupiedTables = tables.filter(t => t.status === 'occupied').length;
    const totalTables = tables.length;
    const activeStaff = staff.filter(s => s.active).length;
    const totalStaff = staff.length;
    const availableMenuItems = menuItems.filter(m => m.available).length;
    const totalMenuItems = menuItems.length;

    return {
      totalOrders,
      completedOrders,
      totalRevenue,
      occupiedTables,
      totalTables,
      activeStaff,
      totalStaff,
      availableMenuItems,
      totalMenuItems,
    };
  } catch (error: any) {
    console.error('Get tenant stats error:', error);
    return null;
  }
};

export default {
  getTenantOrders,
  getTenantMenuItems,
  getTenantMenuCategories,
  getTenantTables,
  getTenantStaff,
  getTenantDailySales,
  getTenantStats,
};

